import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui';
import { api } from '../../lib/api';
import DealIntentBlock from './DealIntentBlock';
import CallInGhl from '../CallInGhl';

export default function OpportunityDrawer({ id, onClose, onSaved }: { id: string; onClose: () => void; onSaved: () => void }) {
  const [opp, setOpp] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [form, setForm] = useState({ name: '', monetaryValue: '', status: '', pipelineStageId: '' });
  const [notes, setNotes] = useState<Array<{ id: string; body: string; dateAdded?: string }>>([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [escrowMsg, setEscrowMsg] = useState('');

  const openEscrow = async () => {
    try {
      const r = await api.openEscrow(id);
      setEscrowMsg(`Opened escrow ✓ (${r.seeded} deadlines)`);
      onSaved();
    } catch (e: any) {
      setEscrowMsg(`Failed: ${e?.message || 'error'}`);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getOpportunity(id);
      const o = res.opportunity;
      setOpp(o);
      setForm({
        name: o.name || '',
        monetaryValue: o.monetary_value == null ? '' : String(o.monetary_value),
        status: (o.status || '').toLowerCase(),
        pipelineStageId: o.stage_id || '',
      });
      // load notes for the linked GHL contact
      if (o.contact_id) {
        api.getContactNotes(o.contact_id)
          .then((r) => setNotes(r.notes || []))
          .catch(() => setNotes([]));
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await api.updateOpportunity(id, {
        name: form.name,
        monetaryValue: form.monetaryValue === '' ? undefined : Number(form.monetaryValue),
        status: form.status,
        pipelineStageId: form.pipelineStageId || undefined,
      });
      setOpp(res.opportunity);
      setMsg({ type: 'ok', text: 'Saved to GoHighLevel' });
      onSaved();
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const mark = async (status: 'won' | 'lost') => {
    setSaving(true); setMsg(null);
    try {
      const res = await api.updateOpportunity(id, { status });
      setOpp(res.opportunity);
      setForm((f) => ({ ...f, status }));
      setMsg({ type: 'ok', text: `Marked ${status}` });
      onSaved();
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Failed' });
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !opp?.contact_id) return;
    setAddingNote(true);
    try {
      const r = await api.addContactNote(opp.contact_id, newNote.trim());
      setNotes((ns) => [{ id: r.note?.id || String(Math.random()), body: newNote.trim() }, ...ns]);
      setNewNote('');
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.message || 'Failed to add note' });
    } finally {
      setAddingNote(false);
    }
  };

  return (
    // z-50, a page dialog's layer: the phone bar is z-40 and comes later in the page, so at z-40 it painted over this
    // drawer's last rows (the note box and its Add) - CHAT-DUET review, 2026-10-28.
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-[420px] max-w-full h-full bg-white shadow-xl overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : !opp ? (
          <div className="text-center py-12 text-gray-500">Not found</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-400">{opp.pipeline_name}</span>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">{opp.name || 'Opportunity'}</h2>
            {opp.ghl_contact_url && (
              <div className="mb-2">
                <CallInGhl url={opp.ghl_contact_url} />
              </div>
            )}

            {/*
              🚨 THE ONLY ROUTE THROUGH TO THE CONTRACT FLOW. Promoting an opportunity to a deal,
              and building an AIR contract from it, both live on `OpportunityDetail`
              (`/crm/opportunities/:id`) - and until this link existed NOTHING in the frontend
              navigated there. Clicking an opportunity opens this drawer, so the whole D26 feature
              was reachable only by typing the URL, which read as "the promote button is missing".
            */}
            <Link
              to={`/crm/opportunities/${id}`}
              className="text-primary-500 hover:underline text-sm font-medium"
            >
              Open full detail — promote to a deal &amp; build a contract →
            </Link>

            {msg && (
              <div
                className={`rounded-lg border p-2 text-sm mb-3 ${
                  msg.type === 'ok'
                    ? 'bg-success-wash border-success/30 text-success'
                    : 'bg-red-50 border-red-200 text-red-700'
                }`}
              >
                {msg.text}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Value (USD)</label>
                  <input
                    type="number"
                    min="0"
                    value={form.monetaryValue}
                    onChange={(e) => setForm((f) => ({ ...f, monetaryValue: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {['open', 'won', 'lost', 'abandoned'].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Stage</label>
                <select
                  value={form.pipelineStageId}
                  onChange={(e) => setForm((f) => ({ ...f, pipelineStageId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {(opp.valid_stages || []).map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save to GoHighLevel'}</Button>
                <button
                  onClick={() => mark('won')}
                  disabled={saving}
                  className="px-3 py-2 border border-success/30 rounded-lg text-sm text-success hover:bg-success-wash"
                >
                  Won
                </button>
                <button
                  onClick={() => mark('lost')}
                  disabled={saving}
                  className="px-3 py-2 border border-red-300 rounded-lg text-sm text-red-700 hover:bg-red-50"
                >
                  Lost
                </button>
                <button
                  onClick={openEscrow}
                  className="px-3 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
                >
                  Open Escrow
                </button>
              </div>
              {escrowMsg && <p className="text-xs text-gray-600 mt-1">{escrowMsg}</p>}
            </div>

            {opp.contact_apex_id && (
              <div className="mt-4 bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase mb-1">Linked contact</p>
                <Link
                  to={`/crm/contacts/${opp.contact_apex_id}`}
                  className="text-primary-500 hover:underline font-medium"
                >
                  {opp.contact_name || 'View contact'}
                </Link>
              </div>
            )}

            {/* Deal intent (persona) block + notes — only when a GHL contact is linked */}
            {opp.contact_id && (
              <>
                <DealIntentBlock
                  ghlId={opp.contact_id}
                  persona={{
                    persona_label: opp.persona_label ?? null,
                    persona_role: opp.persona_role ?? null,
                    persona_intent: opp.persona_intent ?? null,
                    persona_detail: opp.persona_detail ?? null,
                    persona_confidence: opp.persona_confidence ?? null,
                    persona_status: opp.persona_status ?? null,
                  }}
                  onChange={load}
                />

                <div className="mt-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Notes</p>

                  {notes.length === 0 && (
                    <p className="text-xs text-gray-400 mb-2">No notes yet.</p>
                  )}

                  {notes.map((n) => (
                    <div
                      key={n.id}
                      className="text-sm text-gray-700 border-l-2 border-gray-200 pl-2 mb-2 leading-snug"
                    >
                      {/* Strip HTML tags — notes come back as HTML strings */}
                      {n.body.replace(/<[^>]+>/g, '')}
                      {n.dateAdded && (
                        <span className="block text-[10px] text-gray-400 mt-0.5">
                          {new Date(n.dateAdded).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}

                  <div className="flex gap-2 mt-2">
                    <input
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
                      placeholder="Add a note…"
                      className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                    />
                    <button
                      onClick={addNote}
                      disabled={addingNote || !newNote.trim()}
                      className="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      {addingNote ? '…' : 'Add'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
